import React, { useState } from 'react';
import { 
  Shield, Eye, CheckCircle, XCircle, Clock, AlertTriangle,
  Star, TrendingUp, Flame, Award, ExternalLink, Calendar,
  Users, FileText, Globe, Microscope, Search, Filter,
  ChevronDown, ChevronUp, Download, BookOpen, Tag
} from 'lucide-react';
import { useTheme } from '../arrangement/ThemeContext';


// Article Review Card Component
const ArticleReviewCard = ({ article, onApprove, onReject, onViewDetails, actionLoading }) => {
  const [showApprovalOptions, setShowApprovalOptions] = useState(false);
  const [approvalOptions, setApprovalOptions] = useState({
    is_featured: false,
    is_trending: false,
    is_hot: false,
    credibility_rating: 'B+',
    impact_score: 7.0
  });

 const { colors } = useTheme(); 

  const handleQuickApprove = () => {
    onApprove(article.id, {});
  };

  const handleAdvancedApprove = () => {
    onApprove(article.id, approvalOptions);
    setShowApprovalOptions(false);
  };

  return (
    <div style={{
      background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
      borderRadius: '20px',
      padding: '24px',
      border: '1px solid #333',
      transition: 'all 0.3s ease'
    }}>
      <div style={{ display: 'flex', gap: '20px' }}>
        {/* Article Thumbnail */}
        <div style={{
          width: '120px',
          height: '80px',
          borderRadius: '12px',
          background: 'linear-gradient(45deg, #00d4aa20, #10b98120)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '24px',
          border: '1px solid #00d4aa30'
        }}>
          🔬
        </div>

        {/* Article Info */}
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <span style={{
              padding: '4px 8px',
              background: article.status === 'pending' ? '#fbbf2420' : '#ef444420',
              color: article.status === 'pending' ? '#fbbf24' : '#ef4444',
              borderRadius: '8px',
              fontSize: '11px',
              fontWeight: '600',
              textTransform: 'uppercase'
            }}>
              {article.status}
            </span>
            <span style={{
              padding: '4px 8px',
              background: '#3b82f620',
              color: '#3b82f6',
              borderRadius: '8px',
              fontSize: '11px',
              fontWeight: '600'
            }}>
              {article.category}
            </span>
          </div>

          <h3 style={{
            margin: '0 0 8px 0',
            fontSize: '18px',
            fontWeight: '600',
            color: '#e5e5e5',
            lineHeight: '1.3'
          }}>
            {article.title}
          </h3>

          <p style={{
            margin: '0 0 12px 0',
            fontSize: '14px',
            color: '#10b981',
            fontWeight: '500'
          }}>
            {article.subtitle}
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '12px', color: '#888', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Users size={12} />
              {article.author}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={12} />
              {article.institution}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Calendar size={12} />
              {new Date(article.submitted_at).toLocaleDateString()}
            </div>
          </div>

          <p style={{
            margin: '0 0 16px 0',
            fontSize: '14px',
            color: '#bbb',
            lineHeight: '1.5',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}>
            {article.description}
          </p>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: '200px' }}>
          <button
            onClick={() => onViewDetails(article)}
            style={{
              padding: '12px 16px',
              background: 'transparent',
              border: '2px solid #00d4aa',
              borderRadius: '12px',
              color: '#00d4aa',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#00d4aa';
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = '#00d4aa';
            }}
          >
            <Eye size={14} />
            View Details
          </button>

          <div style={{ display: 'flex', gap: '8px' }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <button
                onClick={() => setShowApprovalOptions(!showApprovalOptions)}
                disabled={actionLoading}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: actionLoading ? '#555' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: actionLoading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <CheckCircle size={14} />
                Approve
                <ChevronDown size={12} />
              </button>

              {showApprovalOptions && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: colors.bgCard,
                  border: '1px solid #333',
                  borderRadius: '12px',
                  padding: '16px',
                  marginTop: '8px',
                  zIndex: 10,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
                }}>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '12px',
                      color: '#e5e5e5',
                      marginBottom: '8px',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        checked={approvalOptions.is_featured}
                        onChange={(e) => setApprovalOptions(prev => ({
                          ...prev,
                          is_featured: e.target.checked
                        }))}
                      />
                      <Star size={12} />
                      Featured Article
                    </label>
                    
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '12px',
                      color: '#e5e5e5',
                      marginBottom: '8px',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        checked={approvalOptions.is_trending}
                        onChange={(e) => setApprovalOptions(prev => ({
                          ...prev,
                          is_trending: e.target.checked
                        }))}
                      />
                      <TrendingUp size={12} />
                      Trending
                    </label>
                    
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '12px',
                      color: '#e5e5e5',
                      marginBottom: '12px',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        checked={approvalOptions.is_hot}
                        onChange={(e) => setApprovalOptions(prev => ({
                          ...prev,
                          is_hot: e.target.checked
                        }))}
                      />
                      <Flame size={12} />
                      Hot Topic
                    </label>
                  </div>

                  <div style={{ marginBottom: '12px' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '12px',
                      color: '#888',
                      marginBottom: '4px'
                    }}>
                      Credibility Rating
                    </label>
                    <select
                      value={approvalOptions.credibility_rating}
                      onChange={(e) => setApprovalOptions(prev => ({
                        ...prev,
                        credibility_rating: e.target.value
                      }))}
                      style={{
                        width: '100%',
                        padding: '6px',
                        background: colors.bgTertiary,
                        border: '1px solid #333',
                        borderRadius: '6px',
                        color: '#e5e5e5',
                        fontSize: '12px'
                      }}
                    >
                      <option value="A+">A+ - Exceptional</option>
                      <option value="A">A - Excellent</option>
                      <option value="B+">B+ - Very Good</option>
                      <option value="B">B - Good</option>
                      <option value="C">C - Fair</option>
                    </select>
                  </div>

                  <div style={{ marginBottom: '12px' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '12px',
                      color: '#888',
                      marginBottom: '4px'
                    }}>
                      Impact Score (1-10)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      step="0.1"
                      value={approvalOptions.impact_score}
                      onChange={(e) => setApprovalOptions(prev => ({
                        ...prev,
                        impact_score: parseFloat(e.target.value)
                      }))}
                      style={{
                        width: '100%',
                        padding: '6px',
                        background: colors.bgTertiary,
                        border: '1px solid #333',
                        borderRadius: '6px',
                        color: '#e5e5e5',
                        fontSize: '12px'
                      }}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={handleAdvancedApprove}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        background: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Approve with Options
                    </button>
                    <button
                      onClick={handleQuickApprove}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        background: 'transparent',
                        color: '#888',
                        border: '1px solid #555',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      Quick Approve
                    </button>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => onReject(article.id, 'Rejected by admin')}
              disabled={actionLoading}
              style={{
                padding: '12px 16px',
                background: actionLoading ? '#555' : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                cursor: actionLoading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <XCircle size={14} />
              Reject
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { ArticleReviewCard };